import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links, type CivilDate } from '@eis/core';
import { DetailsSection } from '@eis/patterns';
import {
  categoryLabel,
  describeTerm,
  formatAmount,
  isFromGroup,
  offerableCategories,
  verdictOf,
  type Coverage,
  type CoverageTerm,
} from '../model/claims';

export interface CoveragePanelProps {
  readonly coverage: Coverage;
  /** The damage date the answer is about (YYYY-MM-DD). */
  readonly damageDate: CivilDate;
  readonly locale: string;
  /**
   * The detail's reading (ui-spec §7 "Deckung zum Schadendatum"): the claim's own category term
   * instead of the table of every category a claim could be filed under.
   */
  readonly claimType?: string;
}

/**
 * What a policy covered on the day of a loss (SDD-018 §7 `GetCoverageAtDate`) — the create flow's
 * gate (C-11) and the detail's "Deckung zum Schadendatum" card. The verdict comes first, in words:
 * a refusal names its reason and its date. Then the variant, who answers for the loss (the group's
 * policyholder for a member — "Deckung unter Gruppenvertrag {code}"), the waiting period, the
 * deductible and the category terms; every section the member's group tarif decided is marked
 * "Gruppentarif" (`sources`).
 */
export function CoveragePanel({
  coverage,
  damageDate,
  locale,
  claimType,
}: CoveragePanelProps): ReactNode {
  const { t } = useTranslation('claims');
  const navigate = useNavigate();
  const verdict = verdictOf(coverage);
  const detail = coverage.coverage;
  const currency =
    (coverage.claimConfig as { generalSetting?: { currency?: string } } | undefined)?.generalSetting
      ?.currency ?? 'EUR';
  const money = (amount: number | undefined): string => formatAmount(amount, locale, currency);
  const date = (value: string | undefined): string =>
    value === undefined || value === '' ? '—' : formatCivilDate(value, locale);
  const groupCode = coverage.groupPolicy?.code ?? '';
  const policyCode = coverage.policy?.code ?? '';
  const waiting = detail?.waitingPeriod;

  const groupMark = (section: string): ReactNode =>
    isFromGroup(coverage, section) ? (
      <Chip size="small" variant="outlined" label={t('coverage.fromGroup')} sx={{ ml: 1 }} />
    ) : null;

  const termText = (term: CoverageTerm | undefined): string => {
    const described = describeTerm(term);
    if (described.kind === 'notCovered') return t('coverage.notCovered');
    const period =
      described.period === ''
        ? ''
        : t(`coverage.period.${described.period}` as never, { defaultValue: '' });
    const text =
      described.kind === 'fixed'
        ? t('coverage.fixed', { amount: money(described.amount) })
        : described.limit === undefined
          ? t('coverage.percent', {
              percent: new Intl.NumberFormat(locale, { style: 'percent' }).format(
                described.percent / 100,
              ),
            })
          : t('coverage.percentUpTo', {
              percent: new Intl.NumberFormat(locale, { style: 'percent' }).format(
                described.percent / 100,
              ),
              limit: money(described.limit),
            });
    return period === '' ? text : `${text} ${period}`;
  };

  const refusal =
    verdict === 'IN_WAITING_PERIOD'
      ? t('coverage.refusal.IN_WAITING_PERIOD', { date: date(waiting?.endsOn) })
      : verdict === 'NOT_IN_FORCE'
        ? t('coverage.refusal.NOT_IN_FORCE', { code: policyCode, date: date(damageDate) })
        : verdict === 'NO_SUCH_OBJECT'
          ? t('coverage.refusal.NO_SUCH_OBJECT', { date: date(damageDate) })
          : undefined;

  const policyLink = (code: string, group: boolean): ReactNode => {
    const href = group ? links.insurance.groupPolicy(code) : links.insurance.policy(code);
    return (
      <Link
        href={href}
        sx={{ fontFamily: 'monospace' }}
        onClick={(event) => {
          event.preventDefault();
          void navigate({ href });
        }}
      >
        {code}
      </Link>
    );
  };

  const claimCategory =
    claimType === undefined
      ? undefined
      : (detail?.categories ?? []).find((category) => category.key === claimType);
  const categories = offerableCategories(coverage);
  const deductibles = detail?.deductibles ?? [];

  return (
    <DetailsSection
      title={t('coverage.title')}
      description={t('coverage.asOf', { date: date(damageDate) })}
      fields={[
        {
          label: t('coverage.verdictLabel'),
          value: (
            <span data-testid="coverage-verdict">{t(`coverage.verdict.${verdict}` as never)}</span>
          ),
        },
        ...(policyCode === ''
          ? []
          : [{ label: t('coverage.policy'), value: policyLink(policyCode, false) }]),
        ...(coverage.variant === undefined
          ? []
          : [
              {
                label: t('coverage.variant'),
                value:
                  coverage.variant.label === undefined || coverage.variant.label === ''
                    ? (coverage.variant.key ?? '—')
                    : coverage.variant.label,
              },
            ]),
        ...(coverage.liableParty === undefined
          ? []
          : [
              {
                label: t('coverage.liableParty'),
                value: (
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <span data-testid="liable-party">
                      {coverage.liableParty.displayName === undefined ||
                      coverage.liableParty.displayName === ''
                        ? (coverage.liableParty.partnerId ?? '—')
                        : coverage.liableParty.displayName}
                    </span>
                    {coverage.liableParty.source === 'GROUP' && groupCode !== '' && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('coverage.groupParty', { code: groupCode })}
                      />
                    )}
                  </Stack>
                ),
              },
            ]),
        ...(groupCode === ''
          ? []
          : [
              {
                label: t('coverage.groupPolicy'),
                value: (
                  <span data-testid="group-cover">
                    {t('coverage.groupCover', { code: groupCode })} {policyLink(groupCode, true)}
                  </span>
                ),
              },
            ]),
        ...(detail === undefined
          ? []
          : [
              {
                label: t('coverage.waiting'),
                value: (
                  <>
                    {waiting === undefined || (waiting.days ?? 0) === 0
                      ? t('coverage.waitingNone')
                      : t('coverage.waitingDays', {
                          days: waiting.days ?? 0,
                          date: date(waiting.endsOn),
                        })}
                    {groupMark('waitingPeriod')}
                  </>
                ),
              },
              {
                label: t('coverage.deductibles'),
                value: (
                  <span data-testid="coverage-deductibles">
                    {deductibles.length === 0
                      ? '—'
                      : deductibles
                          .map((deductible) =>
                            t('coverage.deductibleChosen', {
                              label:
                                deductible.label === undefined || deductible.label === ''
                                  ? (deductible.key ?? '')
                                  : deductible.label,
                              amount: money(deductible.chosenOption?.amount),
                            }),
                          )
                          .join(' · ')}
                    {groupMark('deductibles')}
                  </span>
                ),
              },
            ]),
        ...(claimType === undefined || detail === undefined
          ? []
          : [
              {
                label: t('coverage.claimTypeTerm'),
                value: (
                  <span data-testid="claim-type-term">
                    {claimCategory === undefined
                      ? t('coverage.notCovered')
                      : `${categoryLabel(claimCategory)}: ${termText(claimCategory.term)}`}
                    {claimCategory !== undefined &&
                      groupMark(`categories.${claimCategory.key ?? ''}`)}
                  </span>
                ),
              },
            ]),
      ]}
    >
      {refusal !== undefined && (
        <Alert
          severity={verdict === 'IN_WAITING_PERIOD' ? 'warning' : 'error'}
          data-testid="coverage-refusal"
        >
          {refusal}
        </Alert>
      )}
      {verdict === 'COVERED' && detail === undefined && (
        <Alert severity="info">{t('coverage.noDocument')}</Alert>
      )}
      {claimType === undefined && detail !== undefined && categories.length > 0 && (
        <Table size="small" aria-label={t('coverage.categories')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('coverage.category')}</TableCell>
              <TableCell>{t('coverage.term')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.key} data-testid={`coverage-category-${category.key ?? ''}`}>
                <TableCell>{categoryLabel(category)}</TableCell>
                <TableCell>
                  {termText(category.term)}
                  {groupMark(`categories.${category.key ?? ''}`)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </DetailsSection>
  );
}
